import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { Input } from '@components/text-input';
import {
  BackArrowIcon,
  CloseIcon,
  MembersIcon,
  PlusCircleIcon,
  SearchIcon,
  SettingsFilledIcon,
} from '@sketchnote/icons';
import { Avatar, AvatarGroup, Button, SidebarTab, Switch } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import { Emoji } from 'emoji-mart';
import React, { useEffect, useMemo, useState } from 'react';
import { useManageMembersStore } from 'src/components/settings/store';
import { useSidebarStore, Workspace } from 'src/components/sidebar/store';
import { EmojiPopover, handleUpdateFieldsProps } from 'src/features/EmojiPopover';
import { useWorkspaceStore } from 'src/features/workspace/store/useWorkspaceStore';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayoutState } from 'src/shared/hooks/use-layout';
import { shallow } from 'zustand/shallow';

import { useWorkspaceAccess } from '../hooks/useWorkspaceAccess';
import { DeleteWorkspace } from './delete-workspace';
import { ManageMembers } from './manage-members';

export const WorkspaceSettings = ({
  workspaceId,
  onClose,
  selectedMenu,
  handleUpdateWorkspace,
  handleToggleWorkspace,
  handleDeleteWorkspace,
  access,
  onAddWorkspace,
}: {
  handleDeleteWorkspace: (workspace: Workspace) => void;
  handleUpdateWorkspace: (
    name: string,
    description: string,
    icon: any,
    workspaceId: string
  ) => void;
  handleToggleWorkspace: (value: boolean, workspace: Workspace) => void;
  workspaceId: string;
  onClose?: () => void;
  selectedMenu: 'general' | 'members' | 'workspaces';
  access?: string;
  onAddWorkspace?: () => void;
}): JSX.Element => {
  const workspaces = useSidebarStore((s) => s.workspaces, shallow);

  const [activeMenu, setActiveMenu] = useState(selectedMenu);
  const [isDeleteWorkspaceOpen, setIsDeleteWorkspaceOpen] = useState(false);
  const [name, setName] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(
    selectedMenu === 'workspaces' ? '' : workspaceId
  );
  const [isPublicWorkspace, setIsPublicWorkspace] = useState(false);
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const { workspaceModal } = useLayoutState();
  const selectedWorkspace = useMemo<Workspace | undefined>(
    () => workspaces.find((w) => w.workspaceId === selectedWorkspaceId),
    [selectedWorkspaceId]
  );

  const fetchWorkspace = useWorkspaceStore((s) => s.actions.fetchWorkspace);

  useEffect(() => {
    fetchWorkspace(workspaceId);
  }, []);

  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (selectedWorkspace) {
      setIsPublicWorkspace(selectedWorkspace.isPublic);
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    if (selectedWorkspace) {
      setName(selectedWorkspace.name);
      setDescrptionText(selectedWorkspace.description);
      setIcon(selectedWorkspace.icon);
    }
  }, [selectedWorkspaceId]);

  const handleBack = () => {
    if (selectedWorkspaceId) setSelectedWorkspaceId('');
    else onClose?.();
  };
  const handleAddWorkspace = () => {
    onAddWorkspace?.();
    onClose?.();
  };

  const authData = useAuth((s) => s.userDetails);
  const isAdmin = authData?.memberInformation.role === 'admin';
  const { admin, setAdmin } = useManageMembersStore((state) => state);
  const memberRole = authData?.memberInformation?.role;
  const [descrptionText, setDescrptionText] = useState('');
  const [icon, setIcon] = useState<{ id?: any; type?: string }>();
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  const { isFullAccess } = useWorkspaceAccess();

  useEffect(() => {
    if (isAdmin) {
      setAdmin(isAdmin);
    }
  }, [isAdmin]);

  const toggleEmojiPicker = (value?: boolean) => () => {
    setIsEmojiPickerOpen(value ?? !isEmojiPickerOpen);
  };

  const handleWorkspaceNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleMenuClick = (value: 'general' | 'members' | 'workspaces') => {
    setActiveMenu(value);
  };

  const handleDeleteModalOpen = () => {
    setIsDeleteWorkspaceOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteWorkspaceOpen(false);
  };
  const menuItems = [
    {
      items: [
        {
          label: 'General',
          value: 'general',
        },
        {
          label: 'Members',
          value: 'members',
        },
      ],
    },
  ];

  const handleUpdateFields = ({ icon }: handleUpdateFieldsProps) => {
    setIcon({ id: icon?.id, type: icon?.type });
    setIsEmojiPickerOpen(false);
  };

  const isActive = (item: string) => item === activeMenu;
  const accessMenuItems = [
    { title: 'Full access', value: 'full' },
    { title: 'Can edit', value: 'edit' },
    { title: 'Can view', value: 'view' },
    { title: 'Remove', value: 'remove' },
  ];
  const child =
    activeMenu === 'general' ? (
      <>
        <section className="mb-20 pl-16 pr-32 ">
          <h3 className="text-xs font-regular text-gray-600 mb-12">Workspace name</h3>
          <div className="flex mb-16">
            <div className="mr-16">
              {isFullAccess ? (
                <Popover
                  offset={[160, 10]}
                  isOpen={isEmojiPickerOpen}
                  onClose={toggleEmojiPicker(false)}
                  onOpen={toggleEmojiPicker(true)}
                  modifiers={[{ name: 'eventListeners', options: { scroll: isEmojiPickerOpen } }]}
                >
                  <PopoverTrigger>
                    <button className="bg-gray-100 h-48 w-42 border rounded items-center px-10 py-12">
                      {icon?.type === 'image' ? (
                        <img className="w-18 h-18 object-center object-cover" src={icon?.id} />
                      ) : (
                        <Emoji
                          emoji={
                            icon?.id || {
                              id: 'file_folder',
                            }
                          }
                          set="apple"
                          size={18}
                        />
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent>
                    {isEmojiPickerOpen && <EmojiPopover onChangeIcon={handleUpdateFields} />}
                  </PopoverContent>
                </Popover>
              ) : (
                <div className=" py-12  rounded items-center">
                  <Emoji
                    emoji={
                      icon?.id || {
                        id: 'file_folder',
                      }
                    }
                    set="apple"
                    size={18}
                  />
                </div>
              )}
            </div>

            {isFullAccess ? (
              <input
                onChange={handleWorkspaceNameChange}
                className="h-48 p-14 bg-gray-100 rounded border w-full text-sm"
                value={name}
              />
            ) : (
              <p className="py-10">{selectedWorkspace?.name || ''}</p>
            )}
          </div>
          <h3 className="text-xs font-regular mb-12 text-gray-600">Description</h3>
          {isFullAccess ? (
            <textarea
              className="bg-gray-100 text-sm w-full h-90 border rounded-sm p-12 border-gray-200"
              onChange={(e) => setDescrptionText(e.target.value)}
              rows={2}
              value={descrptionText}
              placeholder="Click to add workspace description"
              style={{ height: '90px', resize: 'none' }}
            />
          ) : (
            <p className="">{selectedWorkspace?.description || ''}</p>
          )}
        </section>
        <section className="mt-16 pl-16 pr-32 mb-12">
          <div>
            <div className="text-xs text-gray-600 font-regular">Workspace privacy</div>
            <div className="flex items-center gap-2 mt-8">
              <span className="text-gray-600 text-sm ">Private&nbsp;</span>
              <span className="flex items-center">
                <Switch
                  tooltip={!isFullAccess}
                  tooltipText={
                    'Only the organization admin or users with full access can change the visibility of the workspace'
                  }
                  checked={isPublicWorkspace}
                  disabled={!isFullAccess}
                  onChange={() => setIsSwitchModalOpen(true)}
                />
              </span>
              <span className="text-gray-600 text-sm ">&nbsp;Public</span>
            </div>
          </div>
        </section>
        {isFullAccess && (
          <section className="mt-16 pl-16 pr-32 mb-20">
            <div className="flex gap-12 items-center mb-12">
              <h3 className="text-xs font-regular text-gray-600 ">Delete workspace</h3>
              <span
                className="px-8 py-2 rounded text-xs"
                style={{
                  color: '#E5240B',
                  backgroundColor: '#FEEFED',
                }}
              >
                Danger Zone
              </span>
            </div>
            <p className="text-sm mb-16 text-gray-500">
              Are you sure? You and your team will lose access to this workspace
            </p>
            <Tippy
              content="You cannot delete the only workspace in your organization"
              className="w-auto h-auto bg-gray-999"
              placement="bottom"
              disabled={workspaces?.length > 1}
            >
              <span>
                <button
                  className={cn({
                    'flex py-6 my-auto rounded-sm font-medium items-center w-190 px-10 text-sm':
                      true,
                    'bg-red-800 text-white': workspaces.length > 1,
                    'bg-gray-200 text-gray-400 ': workspaces.length === 1,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDeleteModalOpen();
                  }}
                  disabled={workspaces.length > 1 ? false : true}
                >
                  Delete this workspace
                </button>
              </span>
            </Tippy>
          </section>
        )}

        <Modal isOpen={isDeleteWorkspaceOpen} onClose={handleDeleteModalClose}>
          <ModalOverlay />
          <ModalContent
            style={{
              maxWidth: '46.4rem',
              borderRadius: '0.8rem',
              margin: 'auto',
              backgroundColor: 'white',
              padding: '32px',
            }}
          >
            <ModalCloseButton className="text-xs text-gray-500 mb-24" />
            <ModalBody>
              <DeleteWorkspace
                workspaceName={name}
                onClose={handleDeleteModalClose}
                handleDeleteWorkspace={() =>
                  selectedWorkspace && handleDeleteWorkspace(selectedWorkspace)
                }
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    ) : (
      <ManageMembers
        workspaceName={selectedWorkspace?.name || ''}
        admin={admin}
        workspaceId={workspaceId}
        accessMenuItems={accessMenuItems}
        workspaceaccess={access}
        handleToggleWorkspace={(val) =>
          selectedWorkspace && handleToggleWorkspace(val, selectedWorkspace)
        }
        isPublic={selectedWorkspace?.isPublic}
      />
    );

  const actionButtons = useMemo(() => {
    if (!selectedWorkspace)
      return [
        <Button status="secondary-gray" key={0} onClick={onClose}>
          Done
        </Button>,
      ];
    else {
      if (activeMenu === 'general') {
        return isFullAccess
          ? [
              <Button status="secondary-gray" key={1} onClick={onClose}>
                Discard
              </Button>,
              <Button
                key={2}
                onClick={handleUpdateWorkspace.bind(
                  this,
                  name,
                  descrptionText,
                  icon,
                  selectedWorkspaceId
                )}
              >
                Update
              </Button>,
            ]
          : [];
      } else if (activeMenu === 'members') {
        return [];
      }
    }
  }, [activeMenu, selectedWorkspace, name, descrptionText, icon]);

  const handleToggleWorkspacePrivacy = () => {
    if (!selectedWorkspace) return;
    handleToggleWorkspace(!isPublicWorkspace, selectedWorkspace);
    setIsPublicWorkspace((isPublicWorkspace) => !isPublicWorkspace);
    setIsSwitchModalOpen(false);
  };

  return (
    <div className="rounded-lg w-full flex flex-col h-630 overflow-hidden">
      <div
        className={cn({
          'flex items-center bg-gray-100 border-b border-gray-200 px-32 py-16': true,
          'justify-between': !workspaceModal.showSettings,
          'justify-end': workspaceModal.showSettings,
        })}
      >
        {!workspaceModal.showSettings && (
          <div className="flex items-center gap-12">
            <BackArrowIcon size={32} className="cursor-pointer" onClick={handleBack} />
            Manage workspace
          </div>
        )}

        <div className="flex justify-end h-32 items-center px-20">
          <button aria-label="Close icon" onClick={onClose}>
            <CloseIcon className="text-gray-500 text-xs" />
          </button>
        </div>
      </div>
      <div className="w-full flex-grow min-h-0">
        {selectedWorkspace ? (
          <SidebarTab
            handleMenuClick={handleMenuClick}
            onCloseBtnClick={onClose}
            width={'100%'}
            height={'100%'}
            isActive={isActive}
            menuItems={menuItems}
            headText="Workspace settings"
          >
            {child}
          </SidebarTab>
        ) : (
          <div className="w-full p-32 overflow-auto h-full">
            <div className="flex items-center gap-16 mb-32">
              <Input
                placeholder="Search workspace"
                inputClassName="text-gray-600 font-regular placeholder-gray-500 h-20"
                textSecClassName="flex-grow"
                wrapperClassName="py-6 px-10"
                field="search"
                leftIcon={<SearchIcon wrapperClass="text-gray-600 flex items-center" />}
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
              />
              {memberRole !== 'guest' && (
                <Button onClick={handleAddWorkspace}>
                  <div className="flex gap-8 items-center">
                    <PlusCircleIcon className="text-white" fill="white" /> Add new workspace
                  </div>
                </Button>
              )}
            </div>
            <div className="w-full grid grid-cols-2 gap-16">
              {workspaces.map((w) => {
                if (searchText && !w.name.toLowerCase().includes(searchText.toLowerCase()))
                  return null;
                const isWSAdmin = false;
                const isWSOwner = false;
                return (
                  <div key={w._id} className="border rounded-lg p-12 flex items-start group">
                    <div className="mr-12 p-8 bg-primary-100 rounded-md flex items-center">
                      <Emoji
                        emoji={
                          w.icon || {
                            id: 'file_folder',
                          }
                        }
                        set="apple"
                        size={32}
                      />
                    </div>
                    <div className="flex flex-col gap-6 flex-grow min-w-0">
                      <div className="flex gap-6 flex-grow min-w-0">
                        <h3 className="text-sm font-medium leading-32 mb-4 whitespace-no-wrap truncate min-w-0 flex-grow overflow-hidden">
                          {w.name}
                        </h3>
                        {(isWSAdmin || isWSOwner) && (
                          <span className="text-xs text-gray-500">
                            {isWSOwner ? 'Owner' : 'Admin'}
                          </span>
                        )}
                      </div>
                      <AvatarGroup maxCount={4}>
                        {w.members.map((memberInfo, index) => (
                          <Avatar
                            key={`${memberInfo.member?.firstname}-${index}`}
                            name={memberInfo.member?.firstname}
                            type={memberInfo.member?.avatar ? 'photo' : 'initials'}
                            src={memberInfo.member?.avatar}
                            wrapperClassName="-ml-8 border-2 border-white  transform transition duration-500 hover:scale-125"
                          />
                        ))}
                      </AvatarGroup>
                    </div>
                    <div className="flex items-center gap-8 self-center opacity-0 group-hover:opacity-100">
                      <span
                        className="p-4 rounded-md bg-gray-100 text-gray-700 flex items-center cursor-pointer"
                        onClick={() => {
                          setSelectedWorkspaceId(w.workspaceId);
                          setActiveMenu('general');
                        }}
                      >
                        <SettingsFilledIcon />
                      </span>
                      <span
                        className="p-4 rounded-md bg-gray-100 text-gray-700 flex items-center cursor-pointer"
                        onClick={() => {
                          setSelectedWorkspaceId(w.workspaceId);
                          setActiveMenu('members');
                        }}
                      >
                        <MembersIcon />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {actionButtons && !!actionButtons.length && (
        <div className="flex items-center justify-end bg-gray-100 border-t border-gray-200 px-32 py-16 gap-8">
          {actionButtons.map((b, i) => (
            <span key={i}>{b}</span>
          ))}
        </div>
      )}

      {/** Switch Privacy Modal  */}
      <Modal isOpen={isSwitchModalOpen} onClose={() => setIsSwitchModalOpen(false)}>
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />

          {/* <p className="mb-16 text-base text-gray-999 mt-4">
            
              ? Switching <span>your workspace </span> to private will remove all members. If the workspace was private previously, only the users added initially will have access to this space or the workspace owner. You will have to add new members individually`
              : 'Switching your workspace to public will make it visible to all members in the organization'}
          </p> */}
          {isPublicWorkspace ? (
            <p className="mb-16 text-base text-gray-999 mt-8">
              Switching your workspace to private will remove all members.
              <br />
              <br />
              If the workspace was private previously, only the <b>users added initially</b> will
              have access to this space or the <b>workspace owner</b>. <br /> <br />
              You will have to add new members individually
            </p>
          ) : (
            <p className="mb-16 text-base text-gray-999 mt-4">
              Switching your workspace to public will make it visible to all members in the
              organization
            </p>
          )}

          <div className="flex justify-end mt-12">
            <button
              onClick={() => setIsSwitchModalOpen(false)}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleToggleWorkspacePrivacy}
            >
              Switch
            </button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};
